import { Badge, Box, Group, Stack, Text } from '@mantine/core';

import { ChatMessage, messageTimestamp } from '@/types/chat';

interface MessageBubbleProps {
  message: ChatMessage;
  /** When true, the bubble is rendered as the current user's outbound message
   * (right-aligned, accent background); when false, inbound (left-aligned). */
  isMine: boolean;
  /** When true, this bubble is one of multiple consecutive messages from the
   * same sender — suppresses the sender label and tightens vertical spacing
   * so a run of messages visually clusters. */
  groupedWithPrev?: boolean;
}

/** Per-role visual identity so multi-participant threads read at a glance:
 * the sender header, badge, and inbound bubble tint all share the role hue. */
const ROLE_META: Record<string, { label: string; color: string }> = {
  analyst: { label: 'Admin', color: 'indigo' },
  client: { label: 'Client', color: 'teal' },
  np: { label: 'Partner', color: 'grape' },
  system: { label: 'System', color: 'gray' },
};

/**
 * One message in the chat panel. Mine on the right with a saturated
 * brand bubble + white text; theirs on the left with a role-tinted
 * surface, a prominent sender identity (name + email) and a role badge
 * (Admin / Client / Partner) so it's always obvious who is speaking —
 * especially in multi-participant chats. Internal notes override the
 * surface to a warning-tinted yellow with a visible "Internal" badge so
 * analysts know at a glance the client/NP doesn't see them.
 *
 * Grouped runs (same sender, immediately previous message) drop the
 * sender header and timestamp to declutter the column when one party
 * sends several messages in sequence.
 */
export function MessageBubble({
  message,
  isMine,
  groupedWithPrev = false,
}: Readonly<MessageBubbleProps>) {
  const align = isMine ? 'flex-end' : 'flex-start';
  const role = ROLE_META[message.senderKind] ?? { label: message.senderKind, color: 'gray' };

  // Mine: filled brand surface, white text.
  // Theirs: light surface tinted per sender role, so different
  //   participants get visibly different bubbles.
  // Private (internal): yellow tint overrides regardless of side.
  let bg: string;
  let fg: string;
  let border: string | undefined;
  if (message.isPrivate) {
    bg = 'var(--mantine-color-yellow-1)';
    fg = 'var(--mantine-color-text)';
    border = 'inset 0 0 0 1px var(--mantine-color-yellow-3)';
  } else if (isMine) {
    bg = 'var(--mantine-color-blue-6)';
    fg = 'var(--mantine-color-white)';
  } else {
    bg = `var(--mantine-color-${role.color}-0)`;
    fg = 'var(--mantine-color-text)';
    border = `inset 0 0 0 1px var(--mantine-color-${role.color}-2)`;
  }

  // Asymmetric corner — the corner facing the sender's side is sharper,
  // mimicking a chat-bubble tail.
  const radius = isMine ? '12px 12px 4px 12px' : '12px 12px 12px 4px';

  const senderName = message.senderDisplayName || message.senderEmail || message.senderKind;
  // Show the email alongside the display name when they differ, so the
  // identity is unambiguous even when two participants share a name.
  const senderEmail =
    message.senderEmail && message.senderEmail !== senderName ? message.senderEmail : null;

  // Attachment-only messages render no bubble — an empty padded box reads
  // as a broken blob — but keep the header so the attachments rendered
  // below stay attributed. A message with NO body and NO attachments has
  // nothing to say at all: render nothing (no orphan sender/timestamp).
  const hasBody = Boolean(message.bodyText && message.bodyText.trim());
  const hasAttachments = (message.attachments?.length ?? 0) > 0;
  if (!hasBody && !hasAttachments) {
    return null;
  }

  return (
    <Stack align={align} gap={2} mt={groupedWithPrev ? 2 : 8} mb={2}>
      {!groupedWithPrev && (
        <Group gap="xs" align="baseline" px={4}>
          {!isMine && (
            <>
              <Text size="sm" fw={700} c={`${role.color}.7`}>
                {senderName}
              </Text>
              {senderEmail && (
                <Text size="xs" c="dimmed">
                  {senderEmail}
                </Text>
              )}
              <Badge size="xs" color={role.color} variant="light">
                {role.label}
              </Badge>
            </>
          )}
          {message.isPrivate && (
            <Badge size="xs" color="yellow" variant="light">
              Internal
            </Badge>
          )}
          {messageTimestamp(message) && (
            <Text size="xs" c="dimmed">
              {new Date(messageTimestamp(message) as string).toLocaleString(undefined, {
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Text>
          )}
        </Group>
      )}
      {hasBody && (
        <Box
          bg={bg}
          px="sm"
          py="xs"
          style={{
            borderRadius: radius,
            maxWidth: '78%',
            whiteSpace: 'pre-wrap',
            color: fg,
            // A faint inset outline on theirs gives the bubble a touch of
            // depth without competing with mine's saturated fill.
            boxShadow: border,
          }}
        >
          <Text size="sm" inherit>
            {message.bodyText}
          </Text>
        </Box>
      )}
    </Stack>
  );
}
